import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import CharFadeIn from "../components/CharFadeIn";

// Blog post data (could be moved to a separate file or fetched from an API)
const blogPosts = {
  "how-mounjaro-works": {
    title: "How Mounjaro Works: A Deep Dive into GIP/GLP-1",
    category: "SCIENCE",
    date: "December 10, 2024",
    image: "/images/trends/blog1.png",
    content: `
      <p>Mounjaro (tirzepatide) represents a significant advancement in weight management and type 2 diabetes treatment. Unlike older medications that target a single pathway, Mounjaro is a dual GIP and GLP-1 receptor agonist, meaning it activates two key hormonal pathways simultaneously.</p>

      <h2>The Role of GLP-1</h2>
      <p>Glucagon-like peptide-1 (GLP-1) is a hormone released by the gut after eating. It signals to your brain that you're full, slows down stomach emptying, and stimulates insulin release. This helps control blood sugar spikes after meals and reduces overall appetite.</p>

      <h2>The Role of GIP</h2>
      <p>Glucose-dependent insulinotropic polypeptide (GIP) is another incretin hormone. It enhances insulin secretion and plays a role in fat metabolism. By activating GIP receptors alongside GLP-1, Mounjaro provides a more comprehensive approach to metabolic regulation.</p>

      <h2>The Dual-Agonist Advantage</h2>
      <p>Clinical trials have shown that the dual action of Mounjaro leads to greater weight loss and better blood sugar control compared to medications that target only GLP-1. Participants in the SURMOUNT-1 trial lost an average of 22.5% of their body weight over 72 weeks on the highest dose.</p>

      <h2>What This Means for You</h2>
      <p>The science behind Mounjaro is promising, but it's important to remember that it works best when combined with lifestyle changes. A balanced diet and regular physical activity will enhance your results and help you maintain your progress long-term.</p>
    `,
  },
  "starting-your-journey": {
    title: "Starting Your Mounjaro Journey: What to Expect",
    category: "GETTING STARTED",
    date: "December 8, 2024",
    image: "/images/trends/blog2.png",
    content: `
      <p>Starting a new medication can feel overwhelming. Here's a week-by-week guide to what you can expect during your first few months on Mounjaro.</p>

      <h2>Weeks 1-4: The Beginning</h2>
      <p>You'll start on a 2.5 mg dose, which is a titration dose designed to help your body adjust. During this phase, you may notice reduced appetite, but significant weight loss typically begins later. Some people experience mild nausea—this usually improves as your body adapts.</p>

      <h2>Weeks 5-8: Finding Your Rhythm</h2>
      <p>Your dose will likely increase to 5 mg. Many people start to see more noticeable changes in appetite and early weight loss. This is a good time to establish healthy eating habits, as Mounjaro makes it easier to eat smaller portions.</p>

      <h2>Weeks 9-12 and Beyond</h2>
      <p>Depending on your response, your healthcare provider may increase your dose further (7.5 mg, 10 mg, or higher). By this point, you should have a good understanding of how the medication affects you, and weight loss typically accelerates.</p>

      <h2>Tips for Success</h2>
      <ul>
        <li>Stay hydrated—drink plenty of water throughout the day.</li>
        <li>Eat slowly and stop when you feel satisfied, not stuffed.</li>
        <li>Keep a food journal to track what works for you.</li>
        <li>Communicate with your healthcare provider about any side effects.</li>
      </ul>
    `,
  },
  "managing-side-effects": {
    title: "Managing Common Side Effects of Mounjaro",
    category: "HEALTH TIPS",
    date: "December 5, 2024",
    image: "/images/trends/blog3.png",
    content: `
      <p>Like any medication, Mounjaro can cause side effects. The good news is that most are mild and temporary. Here's how to manage the most common ones.</p>

      <h2>Nausea</h2>
      <p>This is the most frequently reported side effect. To minimize it:</p>
      <ul>
        <li>Eat smaller, more frequent meals.</li>
        <li>Avoid greasy, fried, or very spicy foods.</li>
        <li>Ginger tea or ginger candies can help settle your stomach.</li>
        <li>Don't lie down immediately after eating.</li>
      </ul>

      <h2>Fatigue</h2>
      <p>Some people feel more tired than usual, especially in the early weeks. Ensure you're eating enough calories (don't skip meals even if your appetite is low) and getting adequate sleep.</p>

      <h2>Injection Site Reactions</h2>
      <p>Redness or itching at the injection site is usually mild. Rotate your injection sites (stomach, thigh, upper arm) to minimize irritation. If redness persists or worsens, contact your healthcare provider.</p>

      <h2>When to Seek Help</h2>
      <p>While most side effects are manageable, you should contact your doctor immediately if you experience severe abdominal pain, signs of pancreatitis, or allergic reactions (difficulty breathing, swelling, rash).</p>
    `,
  },
};

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-black mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-100 px-6 md:px-20 py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            className="text-sm text-gray-500 uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {post.category}
          </motion.span>
          <CharFadeIn
            text={post.title}
            className="text-3xl md:text-5xl font-bold text-black mt-4 block"
          />
          <motion.p
            className="text-gray-500 mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {post.date}
          </motion.p>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-6 md:px-20 -mt-12 md:-mt-16">
        <div className="max-w-4xl mx-auto">
          <motion.img
            src={post.image}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          />
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-20 py-12 md:py-16">
        <motion.article
          className="max-w-3xl mx-auto prose prose-lg prose-gray"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </section>

      {/* Back Link */}
      <section className="px-6 md:px-20 pb-16">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-black transition-colors group"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 transform group-hover:-translate-x-1 transition-transform"
              viewBox="0 0 24 24"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogPost;
